import React, { useEffect, useMemo, useState } from "react";
import {
  // Only financially up-to-date members are eligible as referees, so search the
  // up-to-date-only endpoint (aliased to keep the existing call sites).
  useLazySearchUpToDateMembersQuery as useLazySearchMembersQuery,
  useAddProbationerRefereesMutation,
  useUpdateProbationerRefereeMutation,
  useDeleteProbationerRefereeMutation,
} from "../api/probationerApplicationApi";
import {
  Users,
  UserPlus,
  RefreshCcw,
  Trash2,
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  X,
  Fingerprint,
  Loader2,
  ShieldCheck,
  AlertCircle,
} from "lucide-react";

type SelectedMember = {
  id: string;
  surname?: string;
  other_names?: string;
  firstname?: string; // Add this line
  email?: string;
  phone?: string;
};

function fullName(m?: SelectedMember) {
  if (!m) return "—";
  return [m.surname, m.other_names, m?.firstname].filter(Boolean).join(" ");
}

function safeMsg(x: any) {
  return String(
    x?.data?.message || x?.message || x?.error || "Something went wrong"
  );
}

type Decision = "pending" | "approved" | "rejected";

function normalizeDecision(raw: any): Decision {
  const v = String(raw ?? "pending")
    .toLowerCase()
    .trim();
  if (v === "approved") return "approved";
  if (v === "rejected") return "rejected";
  return "pending";
}

function DecisionBadge({ decision }: { decision: any }) {
  const v = normalizeDecision(decision);
  const styles = {
    approved: "bg-green-50 text-green-700 border-green-100",
    rejected: "bg-red-50 text-red-700 border-red-100",
    pending: "bg-amber-50 text-amber-700 border-amber-100",
  };
  const icons = {
    approved: <CheckCircle2 className="w-3 h-3" />,
    rejected: <XCircle className="w-3 h-3" />,
    pending: <Clock className="w-3 h-3" />,
  };

  return (
    <span
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-black uppercase tracking-widest ${styles[v]}`}
    >
      {icons[v]} {v}
    </span>
  );
}

export default function ManageRefereesPanel({
  applicationId,
  referees,
  canEdit,
  onDone,
}: {
  applicationId: string;
  referees: any[];
  canEdit: boolean;
  onDone: () => void;
}) {
  const [trigger, searchState] = useLazySearchMembersQuery();
  const [addReferees, addState] = useAddProbationerRefereesMutation();
  const [updateReferee, updateState] = useUpdateProbationerRefereeMutation();
  const [deleteReferee, deleteState] = useDeleteProbationerRefereeMutation();

  const busy =
    addState.isLoading || updateState.isLoading || deleteState.isLoading;

  const [localError, setLocalError] = useState<string | null>(null);
  const [localSuccess, setLocalSuccess] = useState<string | null>(null);

  const [q, setQ] = useState("");
  const [mode, setMode] = useState<"add" | "replace" | null>(null);
  const [activeRefereeId, setActiveRefereeId] = useState<string | null>(null);
  const [relationship, setRelationship] = useState("");

  useEffect(() => {
    if (!q || q.trim().length < 2) return;
    const t = setTimeout(() => trigger({ q: q.trim() }), 350);
    return () => clearTimeout(t);
  }, [q, trigger]);

  // Defensive: never let a non-array response crash the render (blank page).
  const results = (
    Array.isArray(searchState.data)
      ? searchState.data
      : (searchState.data as any)?.data ?? []
  ) as SelectedMember[];

  const counts = useMemo(() => {
    let approved = 0,
      pending = 0,
      rejected = 0;
    for (const r of referees || []) {
      const d = normalizeDecision(r?.decision);
      if (d === "approved") approved++;
      else if (d === "pending") pending++;
      else if (d === "rejected") rejected++;
    }
    return { approved, pending, rejected, active: approved + pending };
  }, [referees]);

  const canAddMore =
    canEdit && !busy && counts.active < 2 && counts.approved < 2;

  const allMemberIds = useMemo(
    () => new Set((referees || []).map((r: any) => String(r?.member_id))),
    [referees]
  );
  const activeMemberIds = useMemo(
    () =>
      new Set(
        (referees || [])
          .filter((r: any) =>
            ["pending", "approved"].includes(normalizeDecision(r?.decision))
          )
          .map((r: any) => String(r?.member_id))
      ),
    [referees]
  );

  const closeSearch = () => {
    setMode(null);
    setActiveRefereeId(null);
    setRelationship("");
    setQ("");
  };

  const onPickMember = async (m: SelectedMember) => {
    setLocalError(null);
    setLocalSuccess(null);
    const pickedId = String((m as any).member_id ?? m.id);

    if (activeMemberIds.has(pickedId)) {
      setLocalError("This member is already an active referee.");
      return;
    }

    // Search results expose `id` = user_id and `member_id` = the member UUID.
    // Referees are keyed by the member UUID, so send that (fall back to id).
    const memberUuid = (m as any).member_id ?? m.id;

    try {
      if (mode === "add") {
        await addReferees({
          applicationId,
          referees: [
            { member_id: memberUuid, relationship: relationship || undefined },
          ],
        }).unwrap();
      } else if (mode === "replace" && activeRefereeId) {
        await updateReferee({
          applicationId,
          old_referee_id: activeRefereeId,
          new_member_id: memberUuid,
          relationship: relationship || undefined,
        }).unwrap();
      }
      setLocalSuccess("Success");
      closeSearch();
      onDone();
    } catch (e: any) {
      setLocalError(safeMsg(e));
    }
  };

  const onRemove = async (refereeRow: any) => {
    if (!window.confirm("Remove this referee?")) return;
    try {
      await deleteReferee({
        applicationId,
        refereeId: String(refereeRow.id),
      }).unwrap();
      onDone();
    } catch (e: any) {
      setLocalError(safeMsg(e));
    }
  };

  return (
    <div className="bg-white rounded-[32px] overflow-hidden">
      <div className="p-6 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-gray-400" />
          <h3 className="text-xs font-black uppercase tracking-widest text-gray-900">
            Referees Panel
          </h3>
        </div>
        {canAddMore && (
          <button
            onClick={() => {
              setMode("add");
              setLocalError(null);
            }}
            className="p-2 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all shadow-sm"
          >
            <UserPlus className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="p-6 space-y-4">
        {/* Status Mini-Bar */}
        <div className="flex items-center justify-between px-4 py-2 bg-gray-100 rounded-2xl text-[10px] font-bold text-gray-500">
          <span>SLOTS: {counts.active}/2</span>
          <span className="flex gap-2">
            <span className="text-green-600">APP: {counts.approved}</span>
            <span className="text-amber-600">PEN: {counts.pending}</span>
          </span>
        </div>

        {localError && (
          <div className="p-3 bg-red-50 text-red-700 text-xs font-bold rounded-xl border border-red-100 flex items-center gap-2">
            <AlertCircle className="w-3 h-3" /> {localError}
          </div>
        )}

        <div className="space-y-3">
          {referees.length === 0 && (
            <div className="py-10 text-center text-xs text-gray-400 font-medium">
              No referees assigned.
            </div>
          )}

          {referees.map((r: any) => {
            const d = normalizeDecision(r?.decision);
            const isPending = d === "pending";

            return (
              <div
                key={r.id}
                className={`p-4 rounded-2xl border transition-all ${
                  isPending
                    ? "bg-white border-gray-100"
                    : "bg-gray-50 border-transparent opacity-80"
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <DecisionBadge decision={r?.decision} />
                  {canEdit && isPending && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => {
                          setMode("replace");
                          setActiveRefereeId(r.id);
                          setRelationship(r.relationship || "");
                        }}
                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-900 transition-all"
                      >
                        <RefreshCcw className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => onRemove(r)}
                        className="p-2 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-600 transition-all"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="font-bold text-gray-900 text-sm truncate">
                  {r?.member
                    ? `${r.member?.firstname || ""} ${r.member?.surname || ""}`
                    : r.member_id}
                </div>
                <div className="text-[10px] text-gray-400 font-bold uppercase tracking-tight mt-1">
                  Relationship: {r.relationship || "—"}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* SEARCH MODAL OVERLAY */}
      {mode && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white w-full max-w-md rounded-[32px] shadow-2xl overflow-hidden animate-in zoom-in-95">
            <div className="p-6 border-b border-gray-50 flex items-center justify-between">
              <h4 className="font-black text-gray-900 uppercase tracking-tighter">
                {mode === "add" ? "Add Referee" : "Replace Referee"}
              </h4>
              <button
                onClick={closeSearch}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase mb-1 block">
                  Your Relationship
                </label>
                <input
                  className="w-full p-3 bg-gray-50 border border-gray-100 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-gray-900/5 transition-all"
                  placeholder="e.g. Mentor or Colleague"
                  value={relationship}
                  onChange={(e) => setRelationship(e.target.value)}
                />
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" />
                <input
                  autoFocus
                  className="w-full pl-10 pr-4 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-gray-900/5 transition-all font-medium"
                  placeholder="Search by name or ID..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>

              <div className="max-h-[250px] overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                {searchState.isFetching ? (
                  <div className="py-10 flex justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
                  </div>
                ) : (
                  results.map((m) => {
                    const isActive = activeMemberIds.has(String((m as any).member_id ?? m.id));
                    return (
                      <button
                        key={m.id}
                        disabled={isActive || busy}
                        onClick={() => onPickMember(m)}
                        className={`w-full text-left p-4 rounded-2xl border transition-all flex items-center justify-between group
                          ${
                            isActive
                              ? "bg-gray-50 opacity-50 cursor-not-allowed"
                              : "bg-white border-gray-100 hover:border-gray-900 hover:shadow-md"
                          }`}
                      >
                        <div>
                          <div className="font-bold text-gray-900 text-sm">
                            {fullName(m)}
                          </div>
                          <div className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-1 font-mono">
                            <Fingerprint className="w-3 h-3" /> {m.id}
                          </div>
                        </div>
                        {isActive ? (
                          <AlertCircle className="w-4 h-4 text-amber-400" />
                        ) : (
                          <CheckCircle2 className="w-4 h-4 text-gray-100 group-hover:text-gray-900 transition-colors" />
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
